interface Props {
  text: string;
}

// Splits a word problem into a lead paragraph + lettered parts (a. / b. / c.).
// Renders in body font — monospace math-display was wrong for prose.
export default function ProblemStatement({ text }: Props) {
  const parts = parseProblem(text);

  return (
    <div className="card p-5 bg-cream/60 space-y-3">
      <div className="text-xs text-ink/50 uppercase tracking-wide font-semibold">
        What we're solving
      </div>
      {parts.lead && (
        <p className="text-ink/90 leading-relaxed">{parts.lead}</p>
      )}
      {parts.items.length > 0 && (
        <ol className="space-y-2 pl-1">
          {parts.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-coral/15 text-coral font-display font-bold text-sm flex items-center justify-center">
                {item.letter}
              </span>
              <span className="flex-1 text-ink/90 leading-relaxed">{item.body}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function parseProblem(raw: string): { lead: string; items: Array<{ letter: string; body: string }> } {
  const cleaned = raw.replace(/\s+/g, ' ').trim();

  // Match "a.", "b.", "c." etc. followed by a space, at word boundaries.
  // Each letter marker starts a new item.
  const markerRegex = /(?:^|\s)([a-e])\.\s+/gi;
  const markers: Array<{ letter: string; index: number; markerEnd: number }> = [];
  let match;
  while ((match = markerRegex.exec(cleaned)) !== null) {
    markers.push({
      letter: match[1].toLowerCase(),
      index: match.index + (match[0].startsWith(' ') ? 1 : 0),
      markerEnd: markerRegex.lastIndex,
    });
  }

  if (markers.length === 0) {
    return { lead: cleaned, items: [] };
  }

  const lead = cleaned.slice(0, markers[0].index).trim();
  const items = markers.map((m, i) => {
    const end = i + 1 < markers.length ? markers[i + 1].index : cleaned.length;
    return {
      letter: m.letter,
      body: cleaned.slice(m.markerEnd, end).trim(),
    };
  });

  return { lead, items };
}
